import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FileText, Send, Ban, Mail, PenLine, Eye, X, Braces, ChevronDown, AlertTriangle } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../../components/ConfirmDialog';
import { getBranding } from '../../utils/exportBranding';
import {
  PAGE_WIDE, DataTable, DetailDrawer, Dialog, Field, SearchInput, Tabs, OverflowMenu, Timeline, StatusPill,
  fmtDate, fmtDateTime, plural,
} from '../../components/ui';
import { ReasonDialog, useStableCallback, employmentLabel, staffName } from './hrKit';

const STATUS_VIEW = {
  draft:  { label: 'Draft', tone: 'neutral' },
  sent:   { label: 'Awaiting signature', tone: 'warning' },
  signed: { label: 'Signed', tone: 'success' },
  voided: { label: 'Void', tone: 'neutral' },
};
const contractPill = (s) => { const v = STATUS_VIEW[s] || { label: s, tone: 'neutral' }; return <StatusPill status={s} label={v.label} tone={v.tone} />; };

// The placeholders the built-in templates use. A template can declare more of its own.
const DEFAULT_FIELDS = [
  { key: 'employee_name', label: 'Employee full name' },
  { key: 'company_name', label: 'Company name' },
  { key: 'job_title', label: 'Job title' },
  { key: 'employment_type', label: 'Employment type' },
  { key: 'start_date', label: 'Start date' },
  { key: 'pay_rate', label: 'Pay rate' },
  { key: 'award_name', label: 'Award name' },
];
const PLACEHOLDER = /\{\{\s*([a-z0-9_]+)\s*\}\}/gi;
const normName = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();

function ContractText({ children, className = '' }) {
  return <div className={`max-w-prose whitespace-pre-wrap font-sans text-[15px] leading-relaxed text-slate-800 ${className}`}>{children}</div>;
}

/* ── Signing ──────────────────────────────────────────────────────────── */

// Two ways in: the worker signing their own contract, or a worker signing in person on an
// office device — then they type their OWN name, and it must match the name on the contract.
function SignDialog({ contract, inPerson, onClose, onSigned }) {
  const { user } = useAuth();
  const [sig, setSig] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setSig(''); setAgreed(false); setError(''); }, [contract]);
  const close = useStableCallback(() => { if (!saving) onClose(); });
  const signer = inPerson ? (contract && contract.staff_name) : `${user?.first_name || ''} ${user?.last_name || ''}`.trim();

  const sign = async (e) => {
    e.preventDefault();
    if (!sig.trim()) { setError('Type your full name to sign.'); return; }
    if (inPerson && normName(sig) !== normName(contract.staff_name)) { setError(`The name must match the contract: ${contract.staff_name}.`); return; }
    if (!agreed) { setError('Tick the box to confirm you agree to the terms.'); return; }
    setSaving(true);
    try {
      await api.post(`/contracts/${contract.id}/sign`, { signatureData: sig.trim() });
      toast.success('Contract signed');
      setSaving(false);
      onSigned();
    } catch (err) { setError(err.response?.data?.message || "The contract wasn't signed. Try again."); setSaving(false); }
  };

  return (
    <Dialog open={!!contract} onClose={close} size="lg" title={contract ? (inPerson ? `Sign in person: ${contract.title}` : contract.title) : ''}
      description={inPerson && contract ? `Hand this device to ${contract.staff_name}. They read the contract and type their own name to sign.` : undefined}
      footer={(
        <>
          <button type="button" onClick={close} className="btn-secondary" disabled={saving}>Cancel</button>
          <button type="submit" form="contract-sign" className="btn-primary" disabled={saving || !sig.trim() || !agreed}>
            <PenLine size={14} aria-hidden="true" />{saving ? 'Signing…' : 'Sign contract'}
          </button>
        </>
      )}>
      {contract && (
        <form id="contract-sign" onSubmit={sign} className="space-y-4">
          <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50/60 p-4"><ContractText>{contract.content}</ContractText></div>
          <Field label="Your full name" required error={error} hint="Typing your name and signing is a legally binding electronic signature.">
            {(id) => <input id={id} className="input-field" autoComplete="off" placeholder={signer || 'Full name'} value={sig} onChange={(e) => { setSig(e.target.value); setError(''); }} />}
          </Field>
          <label className="flex cursor-pointer items-start gap-2.5 text-[13px] text-slate-700">
            <input type="checkbox" checked={agreed} onChange={(e) => { setAgreed(e.target.checked); setError(''); }} className="mt-0.5 h-4 w-4 rounded" />
            I have read, understood and agree to the terms of this contract.
          </label>
        </form>
      )}
    </Dialog>
  );
}

/* ── Send ─────────────────────────────────────────────────────────────── */

function SendDialog({ open, staff, templates, presetStaffId, onClose, onSent }) {
  const confirm = useConfirm();
  const [form, setForm] = useState({ staffId: '', templateId: '', title: '', content: '' });
  const [errors, setErrors] = useState({});
  const [mode, setMode] = useState('edit');
  const [company, setCompany] = useState('');
  const [saving, setSaving] = useState(false);
  const areaRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    setForm({ staffId: presetStaffId || '', templateId: '', title: '', content: '' });
    setErrors({}); setMode('edit');
    getBranding().then((b) => setCompany((b && b.name) || '')).catch(() => {});
  }, [open, presetStaffId]);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined })); };
  const close = useStableCallback(() => { if (!saving) onClose(); });

  const template = templates.find((t) => t.id === form.templateId);
  const worker = staff.find((s) => s.id === form.staffId);
  const fields = useMemo(() => {
    const extra = Array.isArray(template && template.variables) ? template.variables : [];
    const all = [...DEFAULT_FIELDS, ...extra.filter((v) => v && v.key && !DEFAULT_FIELDS.some((d) => d.key === v.key))];
    return all;
  }, [template]);
  const values = {
    employee_name: worker ? staffName(worker) : '',
    company_name: company,
    job_title: worker ? worker.job_title || '' : '',
    employment_type: worker && worker.employment_type ? employmentLabel(worker.employment_type) : '',
    start_date: worker && worker.start_date ? fmtDate(String(worker.start_date).slice(0, 10)) : '',
  };
  const unresolved = [...new Set([...form.content.matchAll(PLACEHOLDER)].map((m) => m[1]))];
  const fillable = unresolved.filter((k) => values[k]);

  const selectTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    setForm((f) => (t ? { ...f, templateId: tid, title: t.name, content: t.content } : { ...f, templateId: '' }));
    setErrors((e) => ({ ...e, content: undefined, title: undefined }));
  };
  const insertField = (key) => {
    const token = `{{${key}}}`;
    const el = areaRef.current;
    setForm((f) => {
      const start = el ? el.selectionStart : f.content.length;
      const end = el ? el.selectionEnd : f.content.length;
      return { ...f, content: f.content.slice(0, start) + token + f.content.slice(end) };
    });
    setMode('edit');
    setTimeout(() => { if (el) { el.focus(); } }, 0);
  };
  const fillIn = () => {
    setForm((f) => ({ ...f, content: f.content.replace(PLACEHOLDER, (m, k) => (values[k] ? values[k] : m)) }));
    toast.success(`Filled in ${plural(fillable.length, 'field')}`);
  };

  const send = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.staffId) errs.staffId = 'Choose who the contract is for.';
    if (!form.title.trim()) errs.title = 'Give the contract a title.';
    if (!form.content.trim()) errs.content = 'Add the contract text, or start from a template.';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    if (unresolved.length) {
      const ok = await confirm(
        `${worker ? staffName(worker) : 'The worker'} will see these as written: ${unresolved.map((k) => `{{${k}}}`).join(', ')}. Use Preview, then "Fill in", to replace the ones we know.`,
        { title: `Send with ${plural(unresolved.length, 'blank field')}?`, confirmLabel: 'Send anyway' },
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      await api.post('/contracts', form);
      toast.success(`Contract sent to ${worker ? staffName(worker) : 'the staff member'}`);
      setSaving(false);
      onSent();
    } catch (err) { setErrors({ form: err.response?.data?.message || "The contract wasn't sent. Try again." }); setSaving(false); }
  };

  const FieldMenuTrigger = () => <span className="inline-flex items-center gap-1.5"><Braces size={13} aria-hidden="true" />Insert field<ChevronDown size={12} aria-hidden="true" /></span>;
  const previewParts = form.content.split(/(\{\{\s*[a-z0-9_]+\s*\}\})/i);

  return (
    <Dialog open={open} onClose={close} size="lg" title="Send contract"
      description="The worker is emailed and signs with a typed signature."
      footer={(
        <>
          {errors.form && <span className="mr-auto text-[13px] text-rose-700" role="alert">{errors.form}</span>}
          <button type="button" onClick={close} className="btn-secondary" disabled={saving}>Cancel</button>
          <button type="submit" form="contract-send" className="btn-primary" disabled={saving}><Send size={14} aria-hidden="true" />{saving ? 'Sending…' : 'Send contract'}</button>
        </>
      )}>
      <form id="contract-send" onSubmit={send} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Staff member" required error={errors.staffId}>
            {(id) => (
              <select id={id} className="input-field" value={form.staffId} onChange={(e) => set('staffId', e.target.value)}>
                <option value="">Choose…</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{staffName(s)}</option>)}
              </select>
            )}
          </Field>
          <Field label="Template">
            {(id) => (
              <select id={id} className="input-field" value={form.templateId} onChange={(e) => selectTemplate(e.target.value)}>
                <option value="">Start from scratch</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            )}
          </Field>
        </div>
        <Field label="Title" required error={errors.title}>
          {(id) => <input id={id} className="input-field" placeholder="e.g. Employment agreement" value={form.title} onChange={(e) => set('title', e.target.value)} />}
        </Field>
        <Field label="Contract text" required error={errors.content}
          action={(
            <span className="flex items-center gap-2">
              <OverflowMenu label="Insert field" icon={FieldMenuTrigger} className="btn-ghost btn-sm !text-slate-700"
                items={fields.map((f) => ({ label: `${f.label || f.key}  {{${f.key}}}`, onClick: () => insertField(f.key) }))} />
              <Tabs variant="segmented" size="sm" ariaLabel="Contract text view" value={mode} onChange={setMode}
                tabs={[{ id: 'edit', label: 'Edit' }, { id: 'preview', label: 'Preview' }]} />
            </span>
          )}>
          {(id) => (mode === 'edit' ? (
            <textarea id={id} ref={areaRef} className="input-field min-h-[240px] resize-y font-sans text-[15px] leading-relaxed" value={form.content}
              onChange={(e) => set('content', e.target.value)} placeholder="Paste or type the contract. Use Insert field for the worker's name, start date and so on." />
          ) : (
            <div id={id} className="max-h-[360px] min-h-[240px] overflow-y-auto rounded-xl border border-slate-200 bg-white p-4">
              {!worker && <p className="mb-3 text-[12.5px] text-slate-500">Choose a staff member to see their details filled in.</p>}
              <ContractText>
                {previewParts.map((part, i) => {
                  const m = /^\{\{\s*([a-z0-9_]+)\s*\}\}$/i.exec(part);
                  if (!m) return <React.Fragment key={i}>{part}</React.Fragment>;
                  const v = values[m[1]];
                  return v
                    ? <mark key={i} className="rounded bg-primary-50 px-0.5 text-primary-800">{v}</mark>
                    : <mark key={i} className="rounded bg-amber-100 px-0.5 text-amber-900" title="No value for this field">{part}</mark>;
                })}
              </ContractText>
            </div>
          ))}
        </Field>
        {unresolved.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[12.5px] text-slate-600">
            <span>{plural(unresolved.length, 'field')} not filled in yet{fillable.length ? `; ${fillable.length} can be filled from ${worker ? 'their' : 'the'} record` : ''}.</span>
            {fillable.length > 0 && <button type="button" onClick={fillIn} className="btn-secondary btn-sm ml-auto">Fill in {fillable.length}</button>}
          </div>
        )}
      </form>
    </Dialog>
  );
}

/* ── Page ─────────────────────────────────────────────────────────────── */

export default function StaffContracts() {
  const { user, isAdmin, isOffice } = useAuth();
  const canManage = isAdmin || isOffice;
  const [params, setParams] = useSearchParams();
  const [contracts, setContracts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [sendOpen, setSendOpen] = useState(false);
  const [presetStaff, setPresetStaff] = useState('');
  const [viewing, setViewing] = useState(null);
  const [signing, setSigning] = useState(null);      // { contract, inPerson }
  const [voiding, setVoiding] = useState(null);
  const [voidBusy, setVoidBusy] = useState(false);
  const staffFilter = params.get('staff') || '';
  const closeViewer = useStableCallback(() => setViewing(null));

  const load = useCallback(async () => {
    setLoading(true); setLoadError(null);
    try {
      const [c, t, s] = await Promise.all([
        api.get('/contracts').then((r) => r.data),
        canManage ? api.get('/contracts/templates').then((r) => r.data) : Promise.resolve([]),
        canManage ? api.get('/staff').then((r) => r.data) : Promise.resolve([]),
      ]);
      setContracts(Array.isArray(c) ? c : []);
      setTemplates(Array.isArray(t) ? t : []);
      setStaff(Array.isArray(s) ? s : []);
    } catch (e) { setLoadError(e.response?.data?.message || "We couldn't load contracts. Check your connection, then try again."); }
    finally { setLoading(false); }
  }, [canManage]);
  useEffect(() => { load(); }, [load]);

  // /staff/contracts?send=<staffId> (from a staff profile) opens Send with that worker chosen.
  useEffect(() => {
    const sendFor = params.get('send');
    if (!sendFor || !canManage) return;
    setPresetStaff(sendFor);
    setSendOpen(true);
    const p = new URLSearchParams(params); p.delete('send'); setParams(p, { replace: true });
  }, [params, setParams, canManage]);

  const counts = useMemo(() => {
    const base = staffFilter ? contracts.filter((c) => c.staff_id === staffFilter) : contracts;
    const n = { all: base.length, sent: 0, signed: 0, draft: 0, voided: 0 };
    base.forEach((c) => { n[c.status] = (n[c.status] || 0) + 1; });
    return n;
  }, [contracts, staffFilter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contracts.filter((c) => {
      if (staffFilter && c.staff_id !== staffFilter) return false;
      if (status !== 'all' && c.status !== status) return false;
      if (q && !`${c.staff_name || ''} ${c.title || ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [contracts, staffFilter, status, search]);
  const filteredName = staffFilter && (contracts.find((c) => c.staff_id === staffFilter)?.staff_name || staffName(staff.find((s) => s.id === staffFilter)));

  const resend = async (c) => {
    try {
      const { data } = await api.post(`/contracts/${c.id}/resend`);
      toast.success(`Reminder emailed to ${data.email || c.staff_name}`);
    } catch (e) { toast.error(e.response?.data?.message || "The reminder didn't send. Try again."); }
  };
  const doVoid = async (reason) => {
    setVoidBusy(true);
    try {
      await api.post(`/contracts/${voiding.id}/void`, { reason });
      toast.success('Contract voided');
      setVoiding(null); setViewing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.message || "The contract wasn't voided. Try again."); }
    finally { setVoidBusy(false); }
  };

  const isOwn = (c) => c && user && c.staff_id === user.id;
  const actionsFor = (c) => [
    c.status === 'sent' && canManage && { label: 'Resend reminder', icon: Mail, onClick: () => resend(c) },
    c.status === 'sent' && isOwn(c) && { label: 'Review and sign', icon: PenLine, onClick: () => setSigning({ contract: c, inPerson: false }) },
    c.status === 'sent' && canManage && !isOwn(c) && { label: 'Sign in person…', icon: PenLine, onClick: () => setSigning({ contract: c, inPerson: true }) },
    canManage && c.status !== 'voided' && c.status !== 'signed' && { divider: true },
    canManage && c.status !== 'voided' && c.status !== 'signed' && { label: 'Void contract', icon: Ban, danger: true, onClick: () => setVoiding(c) },
  ].filter(Boolean);

  const columns = [
    { key: 'staff', header: 'Staff', sortable: true, sortValue: (c) => c.staff_name || '', render: (c) => (
      <Link to={`/staff/${c.staff_id}`} onClick={(e) => e.stopPropagation()} className="font-medium text-slate-900 hover:text-primary-700 hover:underline">{c.staff_name}</Link>
    ) },
    { key: 'title', header: 'Contract', sortable: true, sortValue: (c) => c.title || '', render: (c) => (
      <div className="min-w-0"><p className="font-medium text-slate-800">{c.title}</p>{c.template_name && <p className="text-[12px] text-slate-500">{c.template_name}</p>}</div>
    ) },
    { key: 'sent', header: 'Sent', sortable: true, defaultDir: 'desc', sortValue: (c) => c.sent_at || null, render: (c) => <span className="whitespace-nowrap tabular-nums">{c.sent_at ? fmtDate(c.sent_at) : '—'}</span> },
    { key: 'status', header: 'Status', sortable: true, sortValue: (c) => c.status, render: (c) => contractPill(c.status) },
    { key: 'signed', header: 'Signed', sortable: true, defaultDir: 'desc', sortValue: (c) => c.signed_at || null, render: (c) => (c.signed_at ? (
      <div className="text-[12.5px]"><p className="whitespace-nowrap tabular-nums text-slate-700">{fmtDateTime(c.signed_at)}</p>{c.signature_data && <p className="text-slate-500">{c.signature_data}</p>}</div>
    ) : <span className="text-slate-400">—</span>) },
    { key: 'actions', header: <span className="sr-only">Actions</span>, align: 'right', render: (c) => (
      <span className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={() => setViewing(c)} className="btn-secondary btn-sm"><Eye size={13} aria-hidden="true" />View</button>
        <OverflowMenu label={`More actions for ${c.title}`} items={actionsFor(c)} />
      </span>
    ) },
  ];

  const tabs = [
    { id: 'all', label: 'All', count: counts.all },
    { id: 'sent', label: 'Awaiting signature', count: counts.sent },
    { id: 'signed', label: 'Signed', count: counts.signed },
    { id: 'draft', label: 'Draft', count: counts.draft },
    { id: 'voided', label: 'Void', count: counts.voided },
  ];

  const timeline = viewing ? [
    { id: 'created', action: 'Created', at: viewing.created_at, tone: 'slate' },
    viewing.sent_at && { id: 'sent', action: `Sent to ${viewing.staff_name}`, at: viewing.sent_at, tone: 'primary' },
    viewing.signed_at && { id: 'signed', action: `Signed as “${viewing.signature_data || viewing.staff_name}”`, at: viewing.signed_at, tone: 'emerald', note: viewing.signed_ip ? `From ${viewing.signed_ip}` : undefined },
    viewing.voided_at && { id: 'voided', action: 'Voided', at: viewing.voided_at, tone: 'rose', note: viewing.void_reason ? `Reason: ${viewing.void_reason}` : 'No reason recorded' },
  ].filter(Boolean) : [];

  return (
    <div className={PAGE_WIDE}>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 lg:flex-row lg:flex-wrap lg:items-center">
          <div className="max-w-full overflow-x-auto"><Tabs variant="segmented" size="sm" ariaLabel="Contract status" tabs={tabs} value={status} onChange={setStatus} /></div>
          <SearchInput value={search} onChange={setSearch} placeholder="Search staff or contract" className="w-full lg:w-64" />
          {staffFilter && (
            <button type="button" onClick={() => { const p = new URLSearchParams(params); p.delete('staff'); setParams(p, { replace: true }); }}
              className="inline-flex h-7 items-center gap-1.5 self-start rounded-full border border-primary-300 bg-primary-50 pl-2.5 pr-2 text-[12px] font-medium text-primary-700 hover:bg-primary-100">
              {filteredName || 'One staff member'}<X size={12} aria-hidden="true" /><span className="sr-only">Show everyone</span>
            </button>
          )}
          <div className="flex items-center gap-3 lg:ml-auto">
            {!loading && counts.sent > 0 && <span className="text-[12.5px] text-slate-600">{plural(counts.sent, 'contract')} awaiting signature</span>}
            {canManage && (
              <button type="button" onClick={() => { setPresetStaff(staffFilter); setSendOpen(true); }} className="btn-primary btn-sm">
                <Send size={14} aria-hidden="true" />Send contract
              </button>
            )}
          </div>
        </div>
        <DataTable
          label="Staff contracts"
          columns={columns}
          rows={filtered}
          loading={loading}
          error={loadError}
          onRetry={load}
          defaultSort={{ key: 'sent', dir: 'desc' }}
          onRowClick={(c) => setViewing(c)}
          empty={contracts.length === 0
            ? { icon: FileText, title: 'No contracts sent yet', hint: 'Send an employment agreement from a template, and the worker signs it electronically.', action: canManage ? <button type="button" onClick={() => { setPresetStaff(''); setSendOpen(true); }} className="btn-primary btn-sm"><Send size={14} aria-hidden="true" />Send contract</button> : undefined }
            : { icon: FileText, title: 'No contracts match', hint: 'Try another status or search.', action: <button type="button" onClick={() => { setStatus('all'); setSearch(''); }} className="btn-secondary btn-sm">Clear filters</button> }}
        />
      </div>

      {/* Read-only view for the office. Only the worker signs (or signs in person). */}
      <DetailDrawer
        open={!!viewing}
        onClose={closeViewer}
        title={viewing ? viewing.title : ''}
        subtitle={viewing ? viewing.staff_name : ''}
        statusNode={viewing ? contractPill(viewing.status) : null}
        meta={viewing ? [
          { label: 'Sent', value: viewing.sent_at ? fmtDate(viewing.sent_at) : null },
          { label: 'Signed', value: viewing.signed_at ? fmtDate(viewing.signed_at) : null },
          { label: 'Template', value: viewing.template_name },
        ] : []}
        footer={viewing && (
          <>
            {viewing.status === 'sent' && isOwn(viewing) && (
              <button type="button" onClick={() => setSigning({ contract: viewing, inPerson: false })} className="btn-primary btn-sm"><PenLine size={13} aria-hidden="true" />Review and sign</button>
            )}
            {viewing.status === 'sent' && canManage && !isOwn(viewing) && (
              <button type="button" onClick={() => setSigning({ contract: viewing, inPerson: true })} className="btn-secondary btn-sm"><PenLine size={13} aria-hidden="true" />Sign in person…</button>
            )}
            {viewing.status === 'sent' && canManage && (
              <button type="button" onClick={() => resend(viewing)} className="btn-secondary btn-sm"><Mail size={13} aria-hidden="true" />Resend reminder</button>
            )}
            {canManage && viewing.status !== 'voided' && viewing.status !== 'signed' && (
              <button type="button" onClick={() => setVoiding(viewing)} className="btn-secondary btn-sm !text-rose-700"><Ban size={13} aria-hidden="true" />Void</button>
            )}
          </>
        )}
      >
        {viewing && (
          <div className="space-y-6">
            {viewing.status === 'voided' && (
              <p className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[13px] text-slate-700">
                <AlertTriangle size={14} className="mt-0.5 shrink-0 text-slate-500" aria-hidden="true" />This contract was voided and can't be signed.
              </p>
            )}
            <section>
              <h4 className="mb-2 text-[12.5px] font-semibold text-slate-500">History</h4>
              <Timeline items={timeline} dense />
            </section>
            <section>
              <h4 className="mb-2 text-[12.5px] font-semibold text-slate-500">Contract</h4>
              <ContractText>{viewing.content}</ContractText>
            </section>
          </div>
        )}
      </DetailDrawer>

      <SendDialog open={sendOpen} staff={staff} templates={templates} presetStaffId={presetStaff}
        onClose={() => setSendOpen(false)} onSent={() => { setSendOpen(false); load(); }} />
      <SignDialog contract={signing && signing.contract} inPerson={!!(signing && signing.inPerson)}
        onClose={() => setSigning(null)} onSigned={() => { setSigning(null); setViewing(null); load(); }} />
      <ReasonDialog
        open={!!voiding}
        onClose={() => setVoiding(null)}
        onConfirm={doVoid}
        busy={voidBusy}
        danger
        required
        title={voiding ? `Void ${voiding.title} for ${voiding.staff_name}?` : ''}
        description="They will no longer be able to sign it. This can't be undone, and the reason is kept on the contract."
        label="Reason for voiding"
        placeholder="e.g. Wrong pay rate — a corrected contract will be sent"
        confirmLabel="Void contract"
      />
    </div>
  );
}
